import { lazy } from 'react';

import { Navigate, Route, Routes } from 'react-router-dom';

import { BonfireLayout } from '@/components/layout/Bonfire/BonfireLayout';
import { DataLayout } from '@/components/layout/Data/DataLayout';
import { DecisionsLayout } from '@/components/layout/Decisions/DecisionsLayout';
import { ExploreLayout } from '@/components/layout/Explore/ExploreLayout';
import { FoundationForecastingLayout } from '@/components/layout/FoundationForecasting/FoundationForecastingLayout';
import { Layout as CanvasLayout } from '@/components/layout/Layout';
import { OverviewLayout } from '@/components/layout/Overview/OverviewLayout';
import { ProtectedRoute } from '@/components/routing/ProtectedRoute';
import { TenantGate } from '@/components/routing/TenantGate';
import { CanvasRoutes, RouterConfig } from '@/configs/routes';
import { Login } from '@/pages/Auth/Login';
import { OAuthCallback } from '@/pages/Auth/OAuthCallback';
import { ComingSoon } from '@/pages/ComingSoon';
import { DataCustomers } from '@/pages/Data/DataCustomers';
import { DataEvents } from '@/pages/Data/DataEvents';
import { DataHome } from '@/pages/Data/DataHome';
import { DataProducts } from '@/pages/Data/DataProducts';
import { DataQuality } from '@/pages/Data/DataQuality';
import { DataTransformations } from '@/pages/Data/DataTransformations';
import { ActivePlanning } from '@/pages/Decisions/ActivePlanning';
import { BacktestDetail } from '@/pages/Decisions/BacktestDetail';
import { BacktestResults } from '@/pages/Decisions/BacktestResults';
import { Canvas } from '@/pages/Decisions/Canvas';
import { Execute } from '@/pages/Decisions/Execute';
import { Policies } from '@/pages/Decisions/Policies';
import { ProductStockingLocationDetail } from '@/pages/Decisions/ProductStockingLocationDetail';
import { ValueOpportunity } from '@/pages/Decisions/ValueOpportunity';
import { ForecastExplorer } from '@/pages/Forecasts/ForecastExplorer';
import { FoundationForecastingHome } from '@/pages/Forecasts/FoundationForecastingHome';
import { NotFound } from '@/pages/NotFound';
import { Cycle as OverviewCycle } from '@/pages/Overview/Cycle';
import { Home as OverviewHome } from '@/pages/Overview/Home';
import { Performance as OverviewPerformance } from '@/pages/Overview/Performance';
import { isFeatureEnabled } from '@/utils/featureFlags';

// Lazy so Bonfire (and CodeMirror) only load when the flag-gated tab is opened.
const BonfireSolutions = lazy(() => import('@/pages/Bonfire/solutions/Solutions'));
const BonfireSolutionCreate = lazy(() => import('@/pages/Bonfire/solutions/SolutionCreate'));
const BonfireSolutionDetail = lazy(() => import('@/pages/Bonfire/solutions/SolutionDetail'));
const BonfireExecutions = lazy(() => import('@/pages/Bonfire/executions/Executions'));
const BonfireExecutionRun = lazy(() => import('@/pages/Bonfire/executions/ExecutionRun'));
const BonfireExecutionDetail = lazy(() => import('@/pages/Bonfire/executions/ExecutionDetail'));
const BonfireModelData = lazy(() => import('@/pages/Bonfire/model_data/ModelData'));
const BonfireModelDataForecastQuery = lazy(() => import('@/pages/Bonfire/model_data/ModelDataForecastQuery'));
const BonfireModelDataEvaluationQuery = lazy(() => import('@/pages/Bonfire/model_data/ModelDataEvaluationQuery'));
const BonfireForecastExplorer = lazy(() => import('@/pages/Bonfire/forecast_explorer/ForecastExplorer'));
const BonfireLeaderboard = lazy(() => import('@/pages/Bonfire/leaderboard/Leaderboard'));

export function App() {
  return (
    <Routes>
      <Route path={RouterConfig[CanvasRoutes.Login].path} element={<Login />} />

      {/* OAuth callback routes */}
      <Route path="/auth/callback" element={<OAuthCallback />} />
      <Route path="/oauth2/callback" element={<OAuthCallback />} />

      {/* Protected routes */}
      <Route element={<ProtectedRoute />}>
        <Route element={<TenantGate />}>
          <Route element={<CanvasLayout />}>
            <Route
              path={RouterConfig[CanvasRoutes.FoundationForecasting].path}
              element={<FoundationForecastingLayout />}
            >
              <Route
                index
                element={<Navigate to={RouterConfig[CanvasRoutes.FoundationForecastingHome].path} replace />}
              />
              <Route
                path={RouterConfig[CanvasRoutes.FoundationForecastingHome].path}
                element={<FoundationForecastingHome />}
              />
              <Route path={RouterConfig[CanvasRoutes.ForecastExplorer].path} element={<ForecastExplorer />} />
            </Route>

            <Route path={RouterConfig[CanvasRoutes.Decisions].path} element={<DecisionsLayout />}>
              <Route index element={<Navigate to={RouterConfig[CanvasRoutes.DecisionsBacktests].path} replace />} />
              <Route path={RouterConfig[CanvasRoutes.DecisionsActivePlanning].path} element={<ActivePlanning />} />
              <Route path={RouterConfig[CanvasRoutes.DecisionsBacktests].path} element={<BacktestResults />} />
              <Route path={RouterConfig[CanvasRoutes.DecisionsBacktestDetail].path} element={<BacktestDetail />} />
              <Route path={RouterConfig[CanvasRoutes.DecisionsExecute].path} element={<Execute />} />
              <Route path={RouterConfig[CanvasRoutes.DecisionsValueOpportunity].path} element={<ValueOpportunity />} />
              <Route
                path={RouterConfig[CanvasRoutes.DecisionsProductStockingLocations].path}
                element={<ProductStockingLocationDetail />}
              />
              <Route path={RouterConfig[CanvasRoutes.DecisionsCanvas].path} element={<Canvas />} />
              <Route path={RouterConfig[CanvasRoutes.DecisionsPolicies].path} element={<Policies />} />
            </Route>

            <Route path={RouterConfig[CanvasRoutes.Overview].path} element={<OverviewLayout />}>
              <Route index element={<Navigate to={RouterConfig[CanvasRoutes.OverviewHome].path} replace />} />
              <Route path={RouterConfig[CanvasRoutes.OverviewHome].path} element={<OverviewHome />} />
              <Route path={RouterConfig[CanvasRoutes.OverviewCycle].path} element={<OverviewCycle />} />
              <Route path={RouterConfig[CanvasRoutes.OverviewPerformance].path} element={<OverviewPerformance />} />
            </Route>

            <Route path={RouterConfig[CanvasRoutes.Data].path} element={<DataLayout />}>
              <Route index element={<Navigate to={RouterConfig[CanvasRoutes.DataHome].path} replace />} />
              <Route path={RouterConfig[CanvasRoutes.DataHome].path} element={<DataHome />} />
              <Route path={RouterConfig[CanvasRoutes.DataCustomers].path} element={<DataCustomers />} />
              <Route path={RouterConfig[CanvasRoutes.DataProducts].path} element={<DataProducts />} />
              <Route path={RouterConfig[CanvasRoutes.DataEvents].path} element={<DataEvents />} />
              <Route path={RouterConfig[CanvasRoutes.DataQuality].path} element={<DataQuality />} />
              <Route path={RouterConfig[CanvasRoutes.DataTransformations].path} element={<DataTransformations />} />
            </Route>
            <Route path={RouterConfig[CanvasRoutes.Explore].path} element={<ExploreLayout />}>
              <Route index element={<Navigate to={RouterConfig[CanvasRoutes.Search].path} replace />} />
              <Route path={RouterConfig[CanvasRoutes.Search].path} element={<ComingSoon />} />
            </Route>

            {isFeatureEnabled('bonfireRoute') && (
              <Route path={RouterConfig[CanvasRoutes.Bonfire].path} element={<BonfireLayout />}>
                <Route index element={<Navigate to={RouterConfig[CanvasRoutes.BonfireSolutions].path} replace />} />
                <Route path={RouterConfig[CanvasRoutes.BonfireSolutions].path} element={<BonfireSolutions />} />
                <Route
                  path={RouterConfig[CanvasRoutes.BonfireSolutionCreate].path}
                  element={<BonfireSolutionCreate />}
                />
                <Route
                  path={RouterConfig[CanvasRoutes.BonfireSolutionDetail].path}
                  element={<BonfireSolutionDetail />}
                />
                {/* Status-change deep-link: renders the same detail view where the promote button lives */}
                <Route
                  path={RouterConfig[CanvasRoutes.BonfireSolutionStatusChange].path}
                  element={<BonfireSolutionDetail />}
                />
                <Route path={RouterConfig[CanvasRoutes.BonfireExecutions].path} element={<BonfireExecutions />} />
                <Route path={RouterConfig[CanvasRoutes.BonfireExecutionRun].path} element={<BonfireExecutionRun />} />
                <Route
                  path={RouterConfig[CanvasRoutes.BonfireExecutionDetail].path}
                  element={<BonfireExecutionDetail />}
                />
                <Route path={RouterConfig[CanvasRoutes.BonfireModelData].path} element={<BonfireModelData />} />
                <Route
                  path={RouterConfig[CanvasRoutes.BonfireModelDataForecastQuery].path}
                  element={<BonfireModelDataForecastQuery />}
                />
                <Route
                  path={RouterConfig[CanvasRoutes.BonfireModelDataEvaluationQuery].path}
                  element={<BonfireModelDataEvaluationQuery />}
                />
                <Route
                  path={RouterConfig[CanvasRoutes.BonfireForecastExplorer].path}
                  element={<BonfireForecastExplorer />}
                />
                <Route path={RouterConfig[CanvasRoutes.BonfireLeaderboard].path} element={<BonfireLeaderboard />} />
              </Route>
            )}

            <Route path="/" element={<Navigate to={RouterConfig[CanvasRoutes.FoundationForecasting].path} replace />} />
            <Route path="/*" element={<NotFound />} />
          </Route>
        </Route>
      </Route>
    </Routes>
  );
}
